/**
 * Три действия у клиента — один сценарий на два экрана.
 *
 * И строка списка, и карточка клиента дают одни и те же «Завести хозяйство» /
 * «Добавить поля» / «Показать поля на карте» (шаг 4 флоу). Логика живёт здесь,
 * а не в каждом экране, потому что порядок вызовов у неё неочевидный и
 * повторять его дважды значит однажды повторить неправильно:
 *
 *   карточки CRM нет → `onboard` (заводит карточку по якорю)
 *   → `setClientFarm` (имя + отдел; кабинет ещё НЕ создаётся)
 *   → если «и добавить поля»: `ensureClientTenant` (вот здесь кабинет)
 *   → `enterClient` (токен с окном клиента) → жёсткий переход на карту.
 *
 * Переход именно жёсткий (`location.href`), а не router.navigate: после
 * `enterClient` сменился токен, и весь загруженный стейт относится к прошлому
 * тенанту — мягкая навигация оставила бы на экране чужие данные.
 */
import { useCallback, useState } from 'react';
import { ApiError } from '@/lib/api';
import { ensureClientTenant, enterClient, setClientFarm } from '@/lib/crm/api';
import { onboardExternalClient } from '@/lib/consultant/api';
import { invalidateConsultantProfile } from '@/lib/consultant/useConsultantProfile';
import type { FarmSetupResult } from '@/components/consultant/FarmSetupDialog';

/** Клиент в терминах действий: минимум, общий для строки и карточки. */
export interface FlowClient {
  /** Якорь внешней записи. `null` — карточка заведена руками в CRM. */
  external_id: string | null;
  /** Наша карточка. `null` — её ещё нет, заведём по дороге. */
  crm_company_id: string | null;
}

export interface ClientFlowHandlers {
  busy: boolean;
  error: string | null;
  clearError: () => void;
  /** Модалка «Завести хозяйство» уже закрыта — выполняем её результат. */
  applyFarmSetup: (client: FlowClient, result: FarmSetupResult) => Promise<boolean>;
  /** «Добавить поля» у клиента, у которого хозяйство уже заведено. */
  goAddFields: (client: FlowClient) => Promise<void>;
  /** «Показать поля на карте». */
  goToMap: (client: FlowClient) => Promise<void>;
}

function message(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

export function useClientFlow(onChanged?: () => void): ClientFlowHandlers {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Карточка CRM под клиента: есть — берём, нет — заводим по якорю. */
  const ensureCard = useCallback(async (client: FlowClient): Promise<string> => {
    if (client.crm_company_id) return client.crm_company_id;
    if (!client.external_id) {
      throw new Error('Нет ни карточки клиента, ни якоря внешней записи');
    }
    const res = await onboardExternalClient(client.external_id);
    return res.crm_company_id;
  }, []);

  const enterAndGo = useCallback(async (cardId: string, addFields: boolean) => {
    await enterClient(cardId);
    invalidateConsultantProfile();
    globalThis.location.href = addFields ? '/map?addField=1' : '/map';
  }, []);

  const applyFarmSetup = useCallback(
    async (client: FlowClient, result: FarmSetupResult) => {
      setBusy(true);
      setError(null);
      try {
        const cardId = await ensureCard(client);
        await setClientFarm(cardId, { name: result.name, cluster_id: result.clusterId });
        if (result.addFields) {
          // Кабинет создаём ЗДЕСЬ, а не в модалке: человек попросил «добавить
          // поля», и схема — техническое условие этого, а не отдельный шаг,
          // о котором его надо спрашивать.
          await ensureClientTenant(cardId);
          await enterAndGo(cardId, true);
        } else {
          onChanged?.();
        }
        return true;
      } catch (e) {
        setError(message(e, 'Не удалось завести хозяйство'));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [ensureCard, enterAndGo, onChanged],
  );

  const goAddFields = useCallback(
    async (client: FlowClient) => {
      setBusy(true);
      setError(null);
      try {
        const cardId = await ensureCard(client);
        await ensureClientTenant(cardId);
        await enterAndGo(cardId, true);
      } catch (e) {
        setError(message(e, 'Не удалось открыть добавление полей'));
        setBusy(false);
      }
    },
    [ensureCard, enterAndGo],
  );

  const goToMap = useCallback(
    async (client: FlowClient) => {
      setBusy(true);
      setError(null);
      try {
        const cardId = await ensureCard(client);
        await enterAndGo(cardId, false);
      } catch (e) {
        setError(message(e, 'Не удалось открыть карту клиента'));
        setBusy(false);
      }
    },
    [ensureCard, enterAndGo],
  );

  return {
    busy,
    error,
    clearError: () => setError(null),
    applyFarmSetup,
    goAddFields,
    goToMap,
  };
}
